import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { getDicoStatus, getHomeDnaStatus, getRemsStatus, getWunderbricksStatus } from "@/components/Ticket/helpers";
import type { TicketCommentPayload } from "@/components/Ticket/TicketCommentField/helpers";
import { TicketAppointmentCard } from "@/components/Ticket/TicketDetails/TicketAppointmentCard";
import { TicketDicoCard } from "@/components/Ticket/TicketDetails/TicketDicoCard";
import { TicketHistoryCard } from "@/components/Ticket/TicketDetails/TicketHistoryCard";
import { TicketHomeDnaCard } from "@/components/Ticket/TicketDetails/TicketHomeDnaCard";
import { TicketInfo } from "@/components/Ticket/TicketDetails/TicketInfoHeader";
import { TicketLog } from "@/components/Ticket/TicketDetails/TicketLog";
import { TicketReminderButton } from "@/components/Ticket/TicketDetails/TicketReminderButton";
import { TicketRemsCard } from "@/components/Ticket/TicketDetails/TicketRemsCard";
import { TicketReporterCard } from "@/components/Ticket/TicketDetails/TicketReporterCard";
import { TicketWunderbricksCard } from "@/components/Ticket/TicketDetails/TicketWunderbricksCard";
import { TicketStatusChangeModal } from "@/components/Ticket/TicketStatusChangeModal";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { isHttpError } from "@/helpers/Network/errors";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { getLocalStorageValue, useUpdateLocalStorage } from "@/hooks/useLocalStorage";
import { useSlug } from "@/hooks/useSlug";
import { DetailLayout } from "@/product-patterns/tickets/DetailLayout";
import { useConfig } from "@/providers/config/useConfig";
import { canSeeServiceHistory, useTicketCommentHandler } from "@/queries/tickets/helpers";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function TicketDetailsPage({ ticketId: ticketIdProp }: { ticketId?: string }): React.ReactNode {
  const { id: paramsTicketId } = useParams<{ id: string }>();
  const ticketId = ticketIdProp ?? paramsTicketId ?? "";
  const [isChangeStatusModalOpened, changeStatusModalHandler] = useBool();
  const [isActivitiesDescending, descendingActivitiesHandlers] = useBool(
    getLocalStorageValue(LOCAL_STORAGE_KEYS.TICKET_DESCENDING_ACTIVITIES, true),
  );

  const slug = useSlug();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const isAiTicketResponsibilitySuggestionAvailable = useConfig("isAiTicketResponsibilitySuggestionAvailable");
  useUpdateLocalStorage(LOCAL_STORAGE_KEYS.TICKET_DESCENDING_ACTIVITIES, isActivitiesDescending);

  const ticketQueries = useTicketQueries();
  const {
    data: ticket,
    isPending: isLoadingTicket,
    error: ticketDetailsError,
  } = useQuery(ticketQueries.detailsAdmin(ticketId));
  const { isFetching: isFetchingResponsibilitySuggestion, isFetched: isFetchedResponsibilitySuggestion } = useQuery({
    ...ticketQueries.responsibilitySuggestion(ticketId),
    enabled: Boolean(ticket?.responsibilityAssignee) && isAiTicketResponsibilitySuggestionAvailable,
  });
  const activitiesQuery = useInfiniteQuery(
    ticketQueries.activitiesInfinite({ ticketId, sortDescending: isActivitiesDescending }),
  );
  const { createComment, editComment } = useTicketCommentHandler(ticketId);

  // Mark as read called everytime ticket is loaded
  const markAsReadMutation = ticketMutations.useMarkTicketAsRead(ticketId);
  const changeStatusMutation = ticketMutations.useUpdateStatus();

  const activities = useMemo(
    () => activitiesQuery.data?.pages.flatMap((x) => x.items) || [],
    [activitiesQuery.data?.pages],
  );

  async function onCreateComment(payload: TicketCommentPayload) {
    if (!ticket) {
      return;
    }

    await createComment({
      ...payload,
      fetchAllActivities: !isActivitiesDescending,
    });

    const isStatusNewOrRejected = ["new", "rejected"].includes(ticket.status.type);
    if (payload.type !== "internal" && isStatusNewOrRejected && ticket.canChangeStatus) {
      changeStatusModalHandler.setTrue();
    }
  }

  useEffect(() => {
    if (isHttpError(ticketDetailsError) && ticketDetailsError.status === 404) {
      void navigate(routes.tickets.list({ slug }));
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.errors.not-found"),
      });
    }
  }, [navigate, showFlashToast, t, ticketDetailsError, slug]);

  const markAsRead = markAsReadMutation.mutate;
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (ticket?.id && ticket.hasUnreadActivity) {
        markAsRead();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [markAsRead, ticket?.hasUnreadActivity, ticket?.hasUnreadComment, ticket?.id]);

  const onToggleActivitySorting = descendingActivitiesHandlers.toggle;
  const isFetchingMoreActivities = activitiesQuery.isFetchingNextPage || activitiesQuery.isPending;
  const hasMoreActivities = activitiesQuery.hasNextPage || false;
  const fetchMoreActivities = activitiesQuery.fetchNextPage;

  const possibleStatuses =
    ticket?.possibleStatuses.map((status) => ({
      id: status.id,
      description: status.name,
      labelColor: status.color,
      defaultStatusId: status.type,
    })) || [];
  const error = ticketDetailsError || activitiesQuery.error;
  const rems = ticket ? getRemsStatus(ticket) : undefined;
  const homeDna = ticket ? getHomeDnaStatus(ticket) : undefined;
  const dico = ticket ? getDicoStatus(ticket) : undefined;
  const wunderbricks = ticket ? getWunderbricksStatus(ticket) : undefined;

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoadingTicket || (!isFetchedResponsibilitySuggestion && isFetchingResponsibilitySuggestion)) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={ticket.title.trim() || t("page.tickets.details.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.tickets.title"),
                to: routes.tickets.list({ slug: slug }),
              },
              {
                name: t("page.tickets.details.title"),
              },
            ]}
          />
        }
        actions={
          <>
            <AskNobiButton
              defaultFilters={{ projectIds: [projectId] }}
              context={{ scope: "project", entity: "ticket", page: "detail", entityId: ticket.id }}
              suggestions={[
                t("component.ask-nobi-chat-drawer.ticket-detail.suggestion.1"),
                t("component.ask-nobi-chat-drawer.ticket-detail.suggestion.2"),
              ]}
            />
            {ticket.canSetReminder && <TicketReminderButton ticket={ticket} />}
          </>
        }
      >
        <DetailLayout
          primary={
            <>
              <TicketInfo ticket={ticket} showProject={false} showDecisionTreePath />
              <TicketLog
                isActivitiesDescending={isActivitiesDescending}
                onToggleActivitySorting={onToggleActivitySorting}
                isFetchingMoreActivities={isFetchingMoreActivities}
                fetchMoreActivities={fetchMoreActivities}
                hasMoreActivities={hasMoreActivities}
                onCreateComment={onCreateComment}
                onEditComment={editComment}
                {...{ activities, ticket, sessionUser }}
              />
            </>
          }
          metadata={
            <>
              <TicketReporterCard ticket={ticket} canNavigate />

              {canSeeServiceHistory(ticket, sessionUser) ? <TicketHistoryCard ticketId={ticket.id} /> : null}

              {rems ? (
                <TicketRemsCard ticketId={ticket.id} ticketIsCollective={ticket.visibility !== "private"} rems={rems} />
              ) : null}

              {homeDna ? <TicketHomeDnaCard ticket={ticket} homeDna={homeDna} /> : null}
              {dico ? <TicketDicoCard ticket={ticket} dico={dico} /> : null}
              {wunderbricks ? <TicketWunderbricksCard ticketId={ticket.id} wunderbricks={wunderbricks} /> : null}
              {ticket.appointments?.hasDetails && <TicketAppointmentCard ticketId={ticket.id} />}
            </>
          }
        />
      </DocumentPage>
      <TicketStatusChangeModal
        isOpened={isChangeStatusModalOpened}
        onOpenChange={changeStatusModalHandler.set}
        onSubmit={async (status) => {
          try {
            if (status.id !== ticket.status.id) {
              await changeStatusMutation.mutateAsync({
                ticketId: ticket.id,
                data: {
                  statusId: status.id,
                  silent: true,
                },
              });
            }
          } finally {
            changeStatusModalHandler.setFalse();
          }
        }}
        targetStatus={possibleStatuses[0]}
        possibleStatuses={possibleStatuses}
      />
    </>
  );
}
